// AdminWidgetsSpec.finance.js — 정산/예약수익/홍보물/흐름 (2026-06-08 회사별 분리)
//
// 회사 무관 (3종 — PINE 전용):
//   admin_reservation_revenue_month / admin_promo_cost_month / admin_cashflow_month_net
//
// 회사별 (PINE/DOT/LNJ 각각 — .finance-companies.js 에서 fan-out):
//   admin_payout_{cc} / admin_consultant_{cc}

import React from 'react';
import {
  Plane, PackageMinus, Activity,
} from 'lucide-react';
import { formatCompactKrw, WIDTHS } from './widgetFormat';
import { FINANCE_COMPANY_SPECS } from './AdminWidgetsSpec.finance-companies';

const monthLabel = (m) => {
  if (!m) return '';
  const [, mm] = String(m).split('-');
  if (!mm) return '';
  return `${Number(mm)}월`;
};

function renderPromoCostMonth(d) {
  if (!d) return '-';
  const revenue = Number(d.revenue || 0);
  const cost = Number(d.cost || 0);
  const profit = Number(d.profit || (revenue - cost));
  const profitClass = profit >= 0
    ? 'text-pink-700 dark:text-pink-300'
    : 'text-amber-700 dark:text-amber-300';
  return (
    <span className="flex flex-col items-end max-w-full gap-0.5">
      <span className="text-[11px] font-normal text-blue-600 dark:text-blue-400 tabular-nums">
        매출 {formatCompactKrw(revenue)}
      </span>
      <span className="text-[11px] font-normal text-red-600 dark:text-red-400 tabular-nums">
        원가 {formatCompactKrw(cost)}
      </span>
      {/* 금액은 축약 없이 전체 표기(10,000,000원)라 천만 단위부터 12자가 된다.
          nowrap 이 없으면 두 줄로 접힌다 — 예약 총수익 위젯과 같은 처리. */}
      <span className={`text-xl font-black tabular-nums leading-none mt-0.5 whitespace-nowrap ${profitClass}`}>
        {formatCompactKrw(profit)}
      </span>
    </span>
  );
}

function renderCashflowMonthNet(d) {
  if (!d) return '-';
  const inflow = Number(d.inflow || 0);
  const outflow = Number(d.outflow || 0);
  const net = Number(d.net || (inflow - outflow));
  const netClass = net >= 0
    ? 'text-emerald-700 dark:text-emerald-300'
    : 'text-amber-700 dark:text-amber-300';
  const netSign = net > 0 ? '+' : '';
  return (
    <span className="flex flex-col items-end max-w-full gap-0.5">
      <span className="text-[11px] font-normal text-blue-600 dark:text-blue-400 tabular-nums">
        들어오는 돈 {formatCompactKrw(inflow)}
      </span>
      <span className="text-[11px] font-normal text-red-600 dark:text-red-400 tabular-nums">
        나가는 돈 {formatCompactKrw(outflow)}
      </span>
      {/* 부호까지 붙어 +10,000,000원 = 13자. nowrap 없으면 두 줄로 접힌다.
          예약 총수익 위젯(아래)과 같은 처리로 맞춘다. */}
      <span className={`text-xl font-black tabular-nums leading-none mt-0.5 whitespace-nowrap ${netClass}`}>
        {netSign}{formatCompactKrw(net)}
      </span>
    </span>
  );
}

function renderReservationRevenueMonth(d) {
  return (
    <span className="flex flex-col items-end max-w-full">
      <span className="text-xl font-black tabular-nums leading-none text-blue-700 dark:text-blue-300 whitespace-nowrap">
        {formatCompactKrw(d?.total)}
      </span>
    </span>
  );
}

export const FINANCE_SPECS = {
  admin_reservation_revenue_month: {
    Icon: Plane, label: '예약 총수익', width: WIDTHS.WIDE, accent: 'blue', size: 'S',
    render: renderReservationRevenueMonth,
    monthNavigate: true,
    monthLabel,
    watermarkIcon: Plane,
  },
  admin_promo_cost_month: {
    Icon: PackageMinus, label: '홍보물', width: WIDTHS.WIDE, accent: 'pink', size: 'S',
    render: renderPromoCostMonth,
    monthNavigate: true,
    monthLabel,
    watermarkIcon: PackageMinus,
  },
  admin_cashflow_month_net: {
    Icon: Activity, label: '자금 흐름', width: WIDTHS.WIDE, accent: 'slate', size: 'S',
    render: renderCashflowMonthNet,
    monthNavigate: true,
    monthLabel,
    watermarkIcon: Activity,
  },
  ...FINANCE_COMPANY_SPECS,
};
